import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { MultiSelectModule } from "primeng/multiselect";
import { SelectModule } from "primeng/select";
import { isObservable, Observable, Subscription } from "rxjs";

interface SelectOption {
  label: string;
  value: string | number;
}

/* 
  Example usage (single selection dropdown):
  {
    key: 'status',
    type: 'select',
    props: {
      label: {
        en: "Dropdown field",
        fi: "Pudotusvalikko",
        sv: "Rullgardinsfält",
      },                                  // Optional
      required: true,
      placeholder: {
        en: "Choose one",
        fi: "Valitse yksi",
        sv: "Välj en",
      },                                  // Optional
      options: [
        {
          label: {
            en: "New",
            fi: "Uusi",
            sv: "Nya",
          },
          value: 'new',
        },
        {
          label: {
            en: "In Progress",
            fi: "Käynnissä",
            sv: "Pågående",
          },
          value: 'in_progress',
        },
        {
          label: {
            en: "Closed",
            fi: "Suljettu",
            sv: "Stängd",
          },
          value: 'closed',
        },
      ],
      description: {
        en: "Choose one of the options",
        fi: "Valitse yksi vaihtoehto",
        sv: "Välj ett alternativ",
      },                                  // Optional
    },
  },


  Multiselect dropdown:
  {
    key: 'status',
    type: 'select',
    props: {
      searchable: true,               // Optional: default is false
      label: {
        en: "Multidropdown field",
        fi: "Monivalintapudotusvalikko",
        sv: "Flervals rullgardinsfält",
      },                                  // Optional
      required: true,
      placeholder: {
        en: "Choose multiple",
        fi: "Valitse useita",
        sv: "Välj flera",
      },                                  // Optional
      options: [
        {
          label: {
            en: "New",
            fi: "Uusi",
            sv: "Nya",
          },
          value: 'new',
        },
        {
          label: {
            en: "In Progress",
            fi: "Käynnissä",
            sv: "Pågående",
          },
          value: 'in_progress',
        },
        {
          label: {
            en: "Closed",
            fi: "Suljettu",
            sv: "Stängd",
          },
          value: 'closed',
        },
      ],
      description: {
        en: "Choose multiple",
        fi: "Valitse useita",
        sv: "Välj flera",
      },                                  // Optional
      multiselect: true,                  // Optional: Enable multiselect
      maxSelectedLabels: 3,               // Optional: Maximum number of selected items to display in field (doesn't affect the return value). Defaults to 20.
      displayType: "comma"                // Optional: Display type for selected items ("chip" or "comma"). Default is "chip"
    },
  },


  ////////////////////

  JSON example:

  Single selection dropdown:
  {
    "key": "status",
    "type": "select",
    "props": {
      "searchable": true, // Optional: default is false
      "label": {
        "en": "Dropdown field",
        "fi": "Pudotusvalikko",
        "sv": "Rullgardinsfält"
      },                                  // Optional
      "required": true,
      "placeholder": {
        "en": "Choose one",
        "fi": "Valitse yksi",
        "sv": "Välj en"
      },                                  // Optional
      "options": [
        {
          "label": {
            "en": "New",
            "fi": "Uusi",
            "sv": "Nya"
          },
          "value": "new"
        },
        {
          "label": {
            "en": "In Progress",
            "fi": "Käynnissä",
            "sv": "Pågående"
          },
          "value": "in_progress"
        },
        {
          "label": {
            "en": "Closed",
            "fi": "Suljettu",
            "sv": "Stängd"
          },
          "value": "closed"
        }
      ],
      "description": {
        "en": "Choose one of the options",
        "fi": "Valitse yksi vaihtoehto",
        "sv": "Välj ett alternativ"
      }                                  // Optional
    }
  },


  Multiselect dropdown:
  {
    "key": "status",
    "type": "select",
    "props": {
      "label": {
        "en": "Multidropdown field",
        "fi": "Monivalintapudotusvalikko",
        "sv": "Flervals rullgardinsfält"
      },                                  // Optional
      "required": true,
      "placeholder": {
        "en": "Choose multiple",
        "fi": "Valitse useita",
        "sv": "Välj flera"
      },                                  // Optional
      "options": [
        {
          "label": {
            "en": "New",
            "fi": "Uusi",
            "sv": "Nya"
          },
          "value": "new"
        },
        {
          "label": {
            "en": "In Progress",
            "fi": "Käynnissä",
            "sv": "Pågående"
          },
          "value": "in_progress"
        },
        {
          "label": {
            "en": "Closed",
            "fi": "Suljettu",
            "sv": "Stängd"
          },
          "value": "closed"
        }
      ],
      "description": {
        "en": "Choose multiple",
        "fi": "Valitse useita",
        "sv": "Välj flera"
      },                                    // Optional
      "multiselect": true,                  // Optional: Enable multiselect
      "maxSelectedLabels": 3,               // Optional: Maximum number of selected items to display in field (doesn't affect the return value). Defaults to 20.
      "displayType": "comma"                // Optional: Display type for selected items ("chip" or "comma"). Default is "chip"
    }
  },
  */

@Component({
  selector: "app-primeng-select",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SelectModule, MultiSelectModule, TranslocoDirective],
  templateUrl: "./select.type.html",
})
export class SelectComponent extends FieldType<FieldTypeConfig> implements OnInit, OnDestroy {
  // this will always be a plain array once we've initialized
  public selectOptions: SelectOption[] = [];
  private options$?: Subscription;

  constructor(private translocoService: TranslocoService) {
    super();
  }

  ngOnInit() {
    const options = this.props.options as SelectOption[] | Observable<SelectOption[]> | undefined;

    if (isObservable(options)) {
      this.options$ = options.subscribe((arr) => {
        this.selectOptions = arr || [];
        const current = this.formControl.value;
        if (current != null) {
          this.formControl.setValue(current);
        }
      });
    } else {
      this.selectOptions = options ?? [];
    }

    if (this.props.required) {
      this.formControl.setValidators([
        ...(this.formControl.validator ? [this.formControl.validator] : []),
        // Angular's built‐in req validator
        (ctrl) => {
          if (this.isMultiselect) {
            return ctrl.value == null || ctrl.value.length === 0 ? { required: true } : null;
          } else {
            return ctrl.value == null || ctrl.value === "" ? { required: true } : null;
          }
        },
      ]);
      this.formControl.updateValueAndValidity({ onlySelf: true });
    }

    // Initialize default value for multiselect
    if (this.isMultiselect && this.formControl.value === null) {
      this.formControl.setValue([]);
    }
  }

  ngOnDestroy() {
    this.options$?.unsubscribe();
  }

  get isMultiselect(): boolean {
    return !!this.props["multiselect"];
  }

  get errorMessage() {
    if (this.formControl.hasError("required")) {
      return (
        this.props["validation"]?.required ||
        this.translocoService.translate("app.formly-types.validationMessages.fieldRequired")
      );
    }
    return this.translocoService.translate("app.formly-types.validationMessages.invalidSelection");
  }
}
